import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { tasksSelectByQuery, boardsSelectors } from '@common-reducers/DBServiceThunks';
import { waGetChatParticipantsTool } from '@extension-context-content/thunks/WhatsappThunks';
// The MentionCandidate type lives in a coupling-free module so the lazy editor
// bundle's MentionPlugin can import it WITHOUT pulling this hook's Redux/thunk
// imports into the bundle. Re-exported here for existing importers.
import type { MentionCandidate } from '../mention-types';
export type { MentionCandidate } from '../mention-types';

/** Cache of group → participants, keyed by group JID. Held in a ref so
 *  it survives re-renders without re-triggering effects. The cache is
 *  per-hook-instance (i.e. per-editor mount); if the user opens a
 *  different scheduler dialog later it's recomputed, which is fine —
 *  participants don't change often and the tool call is cheap. */
type GroupCache = Map<string, MentionCandidate[]>;

const isGroupJid = (id: string | undefined): boolean =>
  chatKindOf(id) === 'group';
const isIndividualJid = (id: string | undefined): boolean =>
  !!id && id.includes('@c.us');
const isWaJid = (id: string | undefined): boolean =>
  isGroupJid(id) || isIndividualJid(id);

/** Pull a display name out of whatever shape a contactList entry has. */
const pickContactDisplay = (c: {
  name?: string;
  formattedTitle?: string;
  firstName?: string;
  lastName?: string;
  id: string;
}): string => {
  const fullName = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  return (
    c.name?.trim() ||
    c.formattedTitle?.trim() ||
    (fullName || '') ||
    c.id.split('@')[0]
  );
};

/**
 * Audience boards store one contact per task. The contact's WA JID lives
 * in `task.customData[phoneColumnId].value` where `phoneColumnId` is
 * declared on the board itself (Board.phoneColumnId, "for campaigns
 * only"). We fall back to "any customData value that looks like a JID"
 * when the column id isn't set or doesn't resolve — older audience
 * boards in the wild aren't all consistent.
 */
const extractWaIdFromTask = (task: any, phoneColumnId?: string): string | null => {
  const cd = task?.customData;
  if (!cd || typeof cd !== 'object') return null;

  if (phoneColumnId && cd[phoneColumnId]?.value) {
    const v = String(cd[phoneColumnId].value).trim();
    if (isWaJid(v)) return v;
  }
  // Fallback: scan all customData cells for a JID-shaped value.
  for (const key of Object.keys(cd)) {
    const v = cd[key]?.value;
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (isWaJid(trimmed)) return trimmed;
    }
  }
  return null;
};

/**
 * Reads the user's currently selected recipients (from BOTH editor
 * surfaces — scheduled message and bulk campaign) and returns the
 * deduped list of `@mention` candidates:
 *
 *   - individual chat (jid ending in `@c.us`) → one candidate.
 *   - group chat       (jid ending in `@g.us`) → all participants of
 *                       that group, fetched lazily via
 *                       `waGetChatParticipantsTool`.
 *
 * Two sources are unioned:
 *
 *   - `AddScheduledMessageButtonReducer.message.contactList`
 *     The scheduler's per-message recipient picker.
 *   - `CampaignReducer.newCampaign.boardIDArray`
 *     Bulk campaigns reference recipients via audience BOARDS. Each
 *     board has tasks; each task holds a WA JID in its `customData`
 *     under the board's `phoneColumnId`. We pull tasks out of
 *     `DBTasksReducer` for each selected board and treat them as
 *     contact entries.
 *
 * `isLoading` is true while any group fetch is in flight, so the UI can
 * render a hint in the dropdown while we resolve participants.
 */
export function useMentionCandidates(): {
  candidates: MentionCandidate[];
  isLoading: boolean;
} {
  // Source 1: scheduler per-message recipient picker.
  const schedulerContactList = useAppSelector(
    (s: any) =>
      (s.AddScheduledMessageButtonReducer?.message?.contactList ?? []) as Array<{
        id: string;
        name?: string;
        formattedTitle?: string;
        firstName?: string;
        lastName?: string;
      }>,
  );

  // Source 2: bulk campaign — audience boards. The state holds boardIDs;
  // we expand each board to its tasks (one task = one contact).
  const campaignBoardIds = useAppSelector(
    (s: any) =>
      (s.CampaignReducer?.newCampaign?.boardIDArray ?? []) as Array<string | number>,
  );

  // For each campaign board, read its phoneColumnId + its tasks. We
  // expose this as a single derived "input list" with the same shape
  // as schedulerContactList so the rest of the hook treats them
  // identically.
  const campaignContactList = useAppSelector((s: any) => {
    if (!Array.isArray(campaignBoardIds) || campaignBoardIds.length === 0) return [];
    const out: Array<{ id: string; name?: string }> = [];
    for (const rawBoardId of campaignBoardIds) {
      const boardId = String(rawBoardId ?? '').trim();
      if (!boardId) continue;
      const board: any = boardsSelectors?.selectById?.(s, boardId);
      const phoneColumnId: string | undefined = board?.phoneColumnId;
      const tasks = tasksSelectByQuery(s.DBTasksReducer, { boardId, deleted: false }) ?? [];
      for (const task of tasks) {
        const waId = extractWaIdFromTask(task, phoneColumnId);
        if (!waId) continue;
        const name = (task?.text && String(task.text).trim()) || undefined;
        out.push({ id: waId, name });
      }
    }
    return out;
  });

  // Unioned input list. Dedup happens later in the candidates memo.
  const contactList = useMemo(
    () => [...schedulerContactList, ...campaignContactList],
    [schedulerContactList, campaignContactList],
  );

  const cacheRef = useRef<GroupCache>(new Map());
  const [, forceRender] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  // Trigger participant fetches for groups we haven't seen yet.
  // Individuals don't need network — they ARE the candidate.
  useEffect(() => {
    console.log('[mention-debug] contactList snapshot', contactList.map((c) => ({
      id: c.id,
      name: (c as any).name,
      isGroup: isGroupJid(c.id),
      isIndividual: isIndividualJid(c.id),
    })));

    const newGroups = contactList
      .map((c) => c.id)
      .filter((id) => isGroupJid(id) && !cacheRef.current.has(id));

    console.log('[mention-debug] groups to fetch', newGroups);

    if (newGroups.length === 0) return;

    let cancelled = false;
    setPendingCount((p) => p + newGroups.length);

    Promise.all(
      newGroups.map(async (groupId) => {
        try {
          console.log('[mention-debug] fetching participants for', groupId);
          const result = await waGetChatParticipantsTool({ chatId: groupId });
          console.log('[mention-debug] tool returned', { groupId, result });
          if (cancelled) return;
          // Tool returns { success: true, data: { participants, total,
          // limit, skip } } via waToolSuccess / waToolError. Participants
          // are normalised to `{ chat_id, name, ... }` shape. The legacy
          // bare-array `data` form is still accepted for back-compat.
          const data: any = (result as any)?.success ? (result as any)?.data : result;
          const participants: any[] =
            Array.isArray(data?.participants)
              ? data.participants
              : Array.isArray(data)
                ? data
                : [];
          console.log('[mention-debug] participants extracted', {
            groupId,
            count: participants.length,
            sample: participants.slice(0, 3),
          });
          const mapped: MentionCandidate[] = participants
            .map((p) => {
              const id: string =
                typeof p?.chatId === 'string'
                  ? p.chatId
                  : typeof p?.id === 'string'
                    ? p.id
                    : typeof p?.id?._serialized === 'string'
                      ? p.id._serialized
                      : '';
              if (!id) return null;
              const display: string =
                (typeof p?.name === 'string' && p.name.trim()) ||
                (typeof p?.displayName === 'string' && p.displayName.trim()) ||
                (typeof p?.contact?.name === 'string' && p.contact.name.trim()) ||
                id.split('@')[0];
              return { id, display };
            })
            .filter((m): m is MentionCandidate => !!m);
          console.log('[mention-debug] mapped candidates', { groupId, mapped });
          cacheRef.current.set(groupId, mapped);
        } catch (err) {
          // Surface for diagnosis but don't crash the editor — falling
          // back to "no candidates" is the correct behaviour when the
          // WA tool isn't reachable (e.g. WA Web disconnected).
          console.warn('[mention-debug] failed to fetch participants for', groupId, err);
          cacheRef.current.set(groupId, []);
        } finally {
          if (!cancelled) setPendingCount((p) => Math.max(0, p - 1));
        }
      }),
    ).then(() => {
      if (!cancelled) forceRender((x) => x + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [contactList]);

  const candidates = useMemo(() => {
    // INTERSECTION semantics (not union):
    //   For each recipient R, compute the set of contacts who would
    //   "see" themselves in a message sent to R:
    //     - R is an individual chat (@c.us) → only R themselves.
    //     - R is a group chat (@g.us)      → all participants of R.
    //   The dropdown shows only contacts that are valid for EVERY
    //   recipient — i.e. the intersection of those sets.
    //
    // Why: the scheduler dispatches one copy per recipient. A mention
    //   to "@Alice" sent to a recipient who doesn't know Alice is a
    //   meaningless ping — Alice never receives it AND the recipient
    //   sees a tag for a name they don't recognise.

    // Display labels for every id we encounter, so the final intersection
    // has a name even when the id came from a group's participant list
    // and not from the recipient list itself.
    const displayMap = new Map<string, string>();
    // One Set per recipient — order doesn't matter; intersection is
    // commutative.
    const perRecipientSets: Set<string>[] = [];
    // If ANY group recipient hasn't loaded yet, we can't reliably
    // intersect — return empty until all groups resolved (the UI will
    // show "Loading…" via isLoading instead).
    let anyGroupStillLoading = false;

    for (const c of contactList) {
      if (isIndividualJid(c.id)) {
        const display = pickContactDisplay(c);
        if (!displayMap.has(c.id)) displayMap.set(c.id, display);
        perRecipientSets.push(new Set([c.id]));
      } else if (isGroupJid(c.id)) {
        const groupParticipants = cacheRef.current.get(c.id);
        if (groupParticipants === undefined) {
          anyGroupStillLoading = true;
          continue;
        }
        const set = new Set<string>();
        for (const p of groupParticipants) {
          set.add(p.id);
          if (!displayMap.has(p.id)) displayMap.set(p.id, p.display);
        }
        perRecipientSets.push(set);
      }
      // Anything else (non-jid id) is ignored — we can't reason about
      // who would see the mention.
    }

    if (anyGroupStillLoading || perRecipientSets.length === 0) {
      console.log('[mention-debug] candidates computed', {
        total: 0,
        reason: anyGroupStillLoading ? 'group_loading' : 'no_recipients',
        pending: pendingCount,
      });
      return [];
    }

    // Intersect all sets. Start from the smallest to keep it cheap.
    perRecipientSets.sort((a, b) => a.size - b.size);
    const intersection = new Set(perRecipientSets[0]);
    for (let i = 1; i < perRecipientSets.length; i++) {
      const next = perRecipientSets[i];
      for (const id of Array.from(intersection)) {
        if (!next.has(id)) intersection.delete(id);
      }
      if (intersection.size === 0) break;
    }

    const out: MentionCandidate[] = [];
    for (const id of intersection) {
      out.push({ id, display: displayMap.get(id) || id.split('@')[0] });
    }

    const sorted = out.sort((a, b) =>
      a.display.toLocaleLowerCase().localeCompare(b.display.toLocaleLowerCase()),
    );
    console.log('[mention-debug] candidates computed (intersection)', {
      total: sorted.length,
      sample: sorted.slice(0, 5),
      sets: perRecipientSets.map((s) => s.size),
      pending: pendingCount,
    });
    return sorted;
    // pendingCount is in the dep list so the memo re-runs when a group
    // fetch resolves and bumps the cache (we forceRender too, but this
    // keeps the contract explicit).
  }, [contactList, pendingCount]);

  return { candidates, isLoading: pendingCount > 0 };
}
